import { useState, useRef, useEffect, KeyboardEvent, useMemo } from 'react'
import debounce from 'debounce'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import styles from './SearchBar.module.scss'
import SearchItem from './search-item/SearchItem'
import { routes } from 'utils/routes'
import { SearchBarProps } from './types'
import axios from 'axios'
import { CMSApiResponseBody } from 'types/cms/request'
import { CmsEncyclopediaArticle } from 'types/cms/collection-types'
import { EncyclopediaArticleModel } from 'components/article/types'
import { mapCmsEncyclopediaArticlesToArticles } from 'utils/cms'
import { Search } from '@boltenergy-be/design-system'
import { useGetLanguage } from 'hooks/useGetLanguage'

const EncyclopediaSearchBar = ({ className, label }: SearchBarProps) => {
  // ROUTER
  const router = useRouter()

  // i18n
  const { t } = useTranslation('common')
  const language = useGetLanguage('uppercase')

  // Local state
  const [value, setValue] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<EncyclopediaArticleModel[] | null>([])
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [selected, setSelected] = useState<{ item: EncyclopediaArticleModel | null; idx: number }>({ idx: -1, item: null })

  // Refs
  const searchInput = useRef<HTMLInputElement>(null)
  const searchList = useRef<HTMLUListElement>(null)

  /**
   * Debounce the search
   */
  const debouncedSearch = useMemo(
    () =>
      debounce(async (value) => {
        try {
          if (!value) {
            return setSearchResults([])
          }

          const {
            data: { data }
          } = await axios.get<CMSApiResponseBody<CmsEncyclopediaArticle[]>>(`/api/cms/encyclopedia-articles?search=${value}`)

          setSearchResults(data ? mapCmsEncyclopediaArticlesToArticles(data.slice(0, 5)) : null)
        } catch (error) {
          setSearchResults(null)
        } finally {
          setIsLoading(false)
        }
      }, 300),
    []
  )

  /**
   * Hides dropdown on click outside
   */
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        searchList.current &&
        !searchList.current.contains(event.target as HTMLUListElement) &&
        searchInput.current &&
        !searchInput.current.contains(event.target as HTMLInputElement)
      ) {
        setIsFocused(false)
        clearSelected()
      }
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  /**
   * Handles the keyboard control of the search component
   *
   * @param {KeyboardEvent<HTMLInputElement>} e
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!!value && isFocused && !!searchResults?.length) {
      if (e.key === 'ArrowDown' || (!e.getModifierState('Shift') && e.key === 'Tab')) {
        e.preventDefault()
        const newIndex = selected.idx + 1 > searchResults.length - 1 ? 0 : selected.idx + 1
        setSelected({ idx: newIndex, item: searchResults[newIndex] })
      }

      if (e.key === 'ArrowUp' || (e.getModifierState('Shift') && e.key === 'Tab')) {
        e.preventDefault()
        const newIndex = selected.idx - 1 < 0 ? searchResults.length - 1 : selected.idx - 1
        setSelected({ idx: newIndex, item: searchResults[newIndex] })
      }

      if (e.key === 'Escape') {
        setValue('')
        clearSelected()
      }

      if (e.key === 'Enter' && selected?.item) {
        const article = selected.item

        return router.push(routes(article.locale, '', [article.category.slug, article.slug]).encyclopediaArticle)
      }
    }
  }

  /**
   * calls the selected clear on text input
   */
  const handleNoneArrowKeyPress = () => {
    if (selected.item || selected.idx !== -1) {
      clearSelected()
    }
  }

  /**
   * function for clearing the selected item
   */
  const clearSelected = () => {
    setSelected({ idx: -1, item: null })
  }

  return (
    <div className={classNames(styles['search-bar'], className)}>
      <div className={styles['search-input-wrapper']}>
        <Search
          language={language}
          label={label}
          ref={searchInput}
          role="searchbox"
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          onKeyPress={handleNoneArrowKeyPress}
          value={value}
          onChange={(event) => {
            setValue(event.currentTarget.value)
            setIsLoading(true)
            debouncedSearch(event.currentTarget.value)
          }}
        />
      </div>
      {!!value && isFocused && (
        <ul ref={searchList} className={styles['search-list']}>
          {isLoading && (
            <li className={classNames(styles['search-item-wrapper'], styles.placeholder)}>
              <span className={styles['placeholder-text']}>{t('searching', 'Aan het zoeken...')}</span>
            </li>
          )}
          {!isLoading && !searchResults?.length && (
            <li className={classNames(styles['search-item-wrapper'], styles.placeholder)}>
              <span className={styles['placeholder-text']}>{t('noResultsFound', 'Geen resultaten gevonden.')}</span>
            </li>
          )}
          {!isLoading &&
            !!searchResults?.length &&
            searchResults.map((article, idx) => (
              <SearchItem key={`${article.slug}-${idx}`} item={article} query={value} selected={idx === selected?.idx} />
            ))}
        </ul>
      )}
    </div>
  )
}

export default EncyclopediaSearchBar
